import { tokens } from "@/art/tokens";

/**
 * 8각 스탯 레이더. 8 축 고정 — str/vit/int/spi/def/spd/cha/tec.
 *
 * values 는 코드 → 수치 맵 (장비·각인 보정 모두 반영된 최종치). maxValue 는 스케일 기준 (기본 40).
 * strongStats 에 포함된 코드는 축 레이블이 ember 로 강조.
 *
 * 2026-04-25 추가: baseValues 가 주어지면 "장비 빼고 본래 스탯" 폴리곤을 inner 로 한 번 더 그린다.
 *   바깥 폴리곤 (values) 의 ember 영역과 안쪽 폴리곤 (baseValues) 의 parchment 영역 사이가
 *   "장비가 더해준 부분" 으로 시각적으로 분리된다.
 *   - innerColor: 본래 스탯 색 (기본 parchmentDim)
 *   - 라벨 수치는 "X (+N)" 형태로 차이를 같이 보여준다.
 */

interface Props {
    values: Record<string, number>;
    /** 장비/각인 보정 빼기 전 본래 수치. 없으면 values 만으로 단일 폴리곤. */
    baseValues?: Record<string, number>;
    strongStats?: string[];
    size?: number;
    maxValue?: number;
}

const STAT_ORDER: Array<{ code: string; label: string }> = [
    { code: "str", label: "완력" },
    { code: "tec", label: "기술" },
    { code: "spd", label: "속력" },
    { code: "cha", label: "매력" },
    { code: "spi", label: "정신" },
    { code: "int", label: "지력" },
    { code: "def", label: "방어" },
    { code: "vit", label: "체력" },
];

export function RadarChart({ values, baseValues, strongStats = [], size = 300, maxValue = 40 }: Props) {
    const n = STAT_ORDER.length;
    const cx = size / 2;
    const cy = size / 2;
    const radius = size / 2 - 34;

    const axisPoint = (i: number, r: number) => {
        const angle = (Math.PI * 2 * i) / n - Math.PI / 2;
        return { x: cx + r * Math.cos(angle), y: cy + r * Math.sin(angle) };
    };

    const levels = [0.25, 0.5, 0.75, 1.0];

    // 값 폴리곤
    const valuePoints = STAT_ORDER.map((s, i) => {
        const raw = values[s.code] ?? 0;
        const norm = Math.max(0, Math.min(1, raw / maxValue));
        const p = axisPoint(i, radius * norm);
        return `${p.x},${p.y}`;
    }).join(" ");

    // 2026-04-25: 본래 스탯(장비 보정 전) 폴리곤 — baseValues 있을 때만.
    const basePoints = baseValues
        ? STAT_ORDER.map((s, i) => {
            const raw = baseValues[s.code] ?? 0;
            const norm = Math.max(0, Math.min(1, raw / maxValue));
            const p = axisPoint(i, radius * norm);
            return `${p.x},${p.y}`;
        }).join(" ")
        : null;
    // 차이 — 장비/각인이 더해준 양 (음수 가능, 표기는 + 만).
    const hasBoost = baseValues
        ? STAT_ORDER.some(s => (values[s.code] ?? 0) > (baseValues[s.code] ?? 0))
        : false;

    return (
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label="스탯 레이더 차트">
            <defs>
                {/* 2026-04-23: 중앙 radial glow — 레이더 내부에 은은한 깊이감. */}
                <radialGradient id="radar-bg-glow" cx="50%" cy="50%" r="60%">
                    <stop offset="0%" stopColor={tokens.color.ember} stopOpacity="0.08" />
                    <stop offset="60%" stopColor={tokens.color.ember} stopOpacity="0.02" />
                    <stop offset="100%" stopColor={tokens.color.ink} stopOpacity="0" />
                </radialGradient>
                {/* 값 폴리곤 그라디언트 — 중앙 밝음, 가장자리 짙음. */}
                <radialGradient id="radar-value-fill" cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor={tokens.color.ember} stopOpacity="0.55" />
                    <stop offset="70%" stopColor={tokens.color.ember} stopOpacity="0.25" />
                    <stop offset="100%" stopColor={tokens.color.ember} stopOpacity="0.12" />
                </radialGradient>
                {/* 2026-04-25: base(본래) 폴리곤 — parchment 톤으로 차분한 inner 영역. */}
                <radialGradient id="radar-base-fill" cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor={tokens.color.parchment} stopOpacity="0.32" />
                    <stop offset="70%" stopColor={tokens.color.parchment} stopOpacity="0.16" />
                    <stop offset="100%" stopColor={tokens.color.parchment} stopOpacity="0.08" />
                </radialGradient>
            </defs>
            {/* 중앙 radial glow */}
            <circle cx={cx} cy={cy} r={radius} fill="url(#radar-bg-glow)" />
            {/* 배경 레벨 폴리곤 (동심 팔각형) */}
            {levels.map((level, li) => {
                const pts = STAT_ORDER.map((_, i) => {
                    const p = axisPoint(i, radius * level);
                    return `${p.x},${p.y}`;
                }).join(" ");
                return (
                    <polygon
                        key={li}
                        points={pts}
                        fill="none"
                        stroke={`${tokens.color.parchmentDim}${li === levels.length - 1 ? "66" : "25"}`}
                        strokeWidth={li === levels.length - 1 ? 1.25 : 1}
                    />
                );
            })}
            {/* 축 라인 */}
            {STAT_ORDER.map((_, i) => {
                const p = axisPoint(i, radius);
                return (
                    <line
                        key={i}
                        x1={cx} y1={cy}
                        x2={p.x} y2={p.y}
                        stroke={`${tokens.color.parchmentDim}30`}
                        strokeWidth={1}
                    />
                );
            })}
            {/* 2026-04-25: 값 폴리곤 (총합) — ember.
                baseValues 가 있으면 그 위에 본래 폴리곤(parchment) 을 그려, 차이가 ring 으로 노출. */}
            <polygon
                points={valuePoints}
                fill="url(#radar-value-fill)"
                stroke={tokens.color.ember}
                strokeWidth={2}
                strokeLinejoin="round"
                style={{ filter: `drop-shadow(0 0 10px ${tokens.color.ember}99)` }}
            />
            {basePoints && (
                <polygon
                    points={basePoints}
                    fill="url(#radar-base-fill)"
                    stroke={tokens.color.parchment}
                    strokeWidth={1.4}
                    strokeOpacity={0.55}
                    strokeDasharray="3 3"
                    strokeLinejoin="round"
                />
            )}
            {/* 값 꼭짓점 — strong stat 은 크고 glow 강함. */}
            {STAT_ORDER.map((s, i) => {
                const raw = values[s.code] ?? 0;
                const norm = Math.max(0, Math.min(1, raw / maxValue));
                const p = axisPoint(i, radius * norm);
                const isStrong = strongStats.includes(s.code);
                return (
                    <g key={s.code}>
                        {isStrong && (
                            <circle
                                cx={p.x} cy={p.y}
                                r={6}
                                fill="none"
                                stroke={tokens.color.ember}
                                strokeOpacity={0.4}
                                strokeWidth={1}
                            />
                        )}
                        <circle
                            cx={p.x} cy={p.y}
                            r={isStrong ? 4 : 3}
                            fill={tokens.color.ember}
                            style={{ filter: isStrong ? `drop-shadow(0 0 6px ${tokens.color.ember})` : undefined }}
                        />
                    </g>
                );
            })}
            {/* 축 레이블 + 수치. baseValues 있으면 "총합 (+장비분)" 를 두 줄로 분리. */}
            {STAT_ORDER.map((s, i) => {
                const labelPoint = axisPoint(i, radius + 18);
                const isStrong = strongStats.includes(s.code);
                const raw = values[s.code] ?? 0;
                const base = baseValues ? (baseValues[s.code] ?? 0) : null;
                const bonus = base !== null ? raw - base : 0;
                return (
                    <g key={s.code}>
                        <text
                            x={labelPoint.x}
                            y={labelPoint.y - 2}
                            textAnchor="middle"
                            dominantBaseline="middle"
                            style={{
                                fill: isStrong ? tokens.color.ember : tokens.color.parchmentDim,
                                fontFamily: tokens.type.display,
                                fontSize: 12,
                                letterSpacing: "0.03em",
                            }}
                        >
                            {s.label}
                        </text>
                        <text
                            x={labelPoint.x}
                            y={labelPoint.y + 11}
                            textAnchor="middle"
                            dominantBaseline="middle"
                            style={{
                                fill: isStrong ? tokens.color.ember : tokens.color.parchment,
                                fontFamily: tokens.type.stat,
                                fontSize: 11,
                            }}
                        >
                            {raw}
                        </text>
                        {/* 2026-04-25: 장비/각인 보정 +N 만 brass 색으로 한 줄 더. 0 이거나 음수면 생략. */}
                        {bonus > 0 && (
                            <text
                                x={labelPoint.x}
                                y={labelPoint.y + 22}
                                textAnchor="middle"
                                dominantBaseline="middle"
                                style={{
                                    fill: tokens.color.brass,
                                    fontFamily: tokens.type.stat,
                                    fontSize: 9.5,
                                    letterSpacing: "0.02em",
                                }}
                            >
                                +{bonus}
                            </text>
                        )}
                    </g>
                );
            })}
            {/* 2026-04-25: 범례 — baseValues 가 있고 실제 보정이 하나라도 있으면 우하단에 "본래 / 장비" 표기.
                정보 과잉 회피 위해 보정 0 일 땐 안 그림. */}
            {baseValues && hasBoost && (
                <g>
                    <line
                        x1={size - 60} y1={size - 18}
                        x2={size - 48} y2={size - 18}
                        stroke={tokens.color.parchment}
                        strokeOpacity={0.55}
                        strokeWidth={1.4}
                        strokeDasharray="3 3"
                    />
                    <text
                        x={size - 46} y={size - 14}
                        style={{
                            fill: tokens.color.parchmentDim,
                            fontFamily: tokens.type.stat,
                            fontSize: 9,
                        }}
                    >
                        본래
                    </text>
                    <line
                        x1={size - 60} y1={size - 6}
                        x2={size - 48} y2={size - 6}
                        stroke={tokens.color.ember}
                        strokeWidth={1.6}
                    />
                    <text
                        x={size - 46} y={size - 2}
                        style={{
                            fill: tokens.color.parchmentDim,
                            fontFamily: tokens.type.stat,
                            fontSize: 9,
                        }}
                    >
                        장비 포함
                    </text>
                </g>
            )}
        </svg>
    );
}
